import React, { useEffect, useState } from "react"

import ContactPartnershipStyles from "./contact-partnership.module.css"
import FlexWrapper from "../../../shared/components/fields/flex-wrapper"
import IconAction from "../../../shared/components/universal/icon-action"
import Input from "../../../shared/components/fields/input"
import Radio from "../../../shared/components/fields/radio"
import Text from "../matrix/text"
import Textarea from "../../../shared/components/fields/textarea"
import { errorToast } from "../../../shared/utils/error-toast"
import { genders } from "../../../shared/config/genders"
import { scrollTo } from "../../../shared/utils/smooth-scroll"
import { useForm } from "react-hook-form"
import { usePostContact } from "../../../hooks/contact/use-query-contact"
import { useTranslation } from "react-i18next"

/* global exponea */

const ContactPartnership = () => {
    const { t } = useTranslation("translations")

    const [isSuccess, setIsSuccess] = useState(false)

    useEffect(() => {
        if (isSuccess) {
            scrollTo("success_message")
        }
    }, [isSuccess])

    const mutate = usePostContact()

    const choices = [
        {
            label: t("contact_partnership_form_contact_preference_option_1"),
            value: t("contact_partnership_form_contact_preference_option_1"),
        },
        {
            label: t("contact_partnership_form_contact_preference_option_2"),
            value: t("contact_partnership_form_contact_preference_option_2"),
        },
    ]

    const {
        handleSubmit,
        control,
        reset,
        register,
        formState: { errors },
    } = useForm({
        mode: "onTouched",
        defaultValues: {
            salutation: "",
            firstname: "",
            lastname: "",
            street: "",
            street_no: "",
            postcode: "",
            city: "",
            phone: "",
            email: "",
            job: "",
            choice: choices[0].value,
            request: "",
            further_information: "",
        },
        context: undefined,
        criteriaMode: "firstError",
        shouldFocusError: true,
    })

    const submit = async values => {
        const body = {
            category_id: process.env.CONTACT_PARTNER_CATEGORY_ID,
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
            phone: values.phone,
            body: t("contact_partnership_form_submit_body", {
                salutation: t(genders.find(g => g.id === values.salutation).name),
                firstname: values.firstname,
                lastname: values.lastname,
                street: values.street,
                street_no: values.street_no,
                postcode: values.postcode,
                city: values.city,
                job: values.job,
                phone: values.phone,
                email: values.email,
                choice: choices.find(c => c.value === values.choice).label,
            }),
        }

        if (values.request) {
            body.body += t("contact_partnership_form_submit_body_requested_date", { requested_date: values.request })
        }

        if (values.further_information) {
            body.body += t("contact_partnership_form_submit_body_further_information", {
                further_information: values.further_information,
            })
        }

        try {
            await mutate.mutateAsync(body)
            setIsSuccess(true)
            if (exponea.isLoaded) {
                exponea.track("submit", { form: "partnerschaft" })
            }
            reset()
        } catch (error) {
            errorToast(error)
        }
    }

    return (
        <div className={ContactPartnershipStyles.wrapper} id="wrapper">
            {!isSuccess ? (
                <form
                    onSubmit={handleSubmit(submit)}
                    name="contact_partnership"
                    className={ContactPartnershipStyles.form}
                >
                    <h3>{t("contact_partnership_form_contact_details")}</h3>
                    <Radio
                        register={register("salutation", {
                            required: { value: true, message: t("form_error_required") },
                        })}
                        error={errors}
                        options={genders.map(g => ({
                            label: t(g.name),
                            value: g.id,
                        }))}
                        label={t("contact_partnership_form_salutation")}
                        flex
                        required
                    />
                    <FlexWrapper flex={["2 1 auto", "2 1 auto"]} wrap>
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="firstname"
                            label={t("contact_form_firstname_label")}
                            autocomplete="given-name"
                            required
                        />
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="lastname"
                            label={t("contact_form_lastname_label")}
                            autocomplete="family-name"
                            required
                        />
                    </FlexWrapper>
                    <FlexWrapper childWidths={[70, 30]}>
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="street"
                            label={t("contact_partnership_form_street")}
                            autocomplete="address-line1"
                            required
                        />
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="street_no"
                            label={t("contact_partnership_form_street_no")}
                            autoSize
                            required
                        />
                    </FlexWrapper>
                    <FlexWrapper childWidths={[30, 70]}>
                        <Input
                            control={control}
                            rules={{
                                required: { value: true, message: t("form_error_required") },
                            }}
                            type="text"
                            name="postcode"
                            label={t("contact_partnership_form_postcode")}
                            autocomplete="postal-code"
                            autoSize
                            required
                        />
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="text"
                            name="city"
                            label={t("contact_partnership_form_city")}
                            autocomplete="address-level2"
                            required
                        />
                    </FlexWrapper>
                    <FlexWrapper flex={["2 1 auto", "2 1 auto"]} wrap>
                        <Input
                            rules={{
                                required: { value: true, message: t("form_error_required") },
                            }}
                            control={control}
                            type="tel"
                            name="phone"
                            autocomplete="tel"
                            label={t("contact_partnership_form_phone")}
                            required
                        />
                        <Input
                            control={control}
                            rules={{ required: { value: true, message: t("form_error_required") } }}
                            type="email"
                            name="email"
                            label={t("contact_partnership_form_email")}
                            autocomplete="email"
                            required
                        />
                    </FlexWrapper>
                    <Input
                        control={control}
                        rules={{ required: { value: true, message: t("form_error_required") } }}
                        type="text"
                        name="job"
                        label={t("contact_partnership_form_job")}
                        required
                        className={ContactPartnershipStyles.job}
                    />
                    <h3 className={ContactPartnershipStyles.preference}>
                        {t("contact_partnership_form_contact_preference")}
                    </h3>
                    <Radio
                        register={register("choice", {
                            required: { value: true, message: t("form_error_required") },
                        })}
                        flex
                        options={choices.map(c => ({
                            label: c.label,
                            value: c.value,
                        }))}
                    />
                    <h3 className={ContactPartnershipStyles.request}>{t("contact_partnership_form_requested_date")}</h3>
                    <Input
                        control={control}
                        rules={{
                            required: { value: false },
                        }}
                        type="text"
                        name="request"
                        length={80}
                        showLengthInfo
                    />
                    <h3>{t("contact_partnership_form_further_information")}</h3>
                    <Textarea
                        control={control}
                        rules={{
                            required: { value: false },
                        }}
                        type="textarea"
                        name="further_information"
                        length={400}
                        showLengthInfo
                    />
                    <div className={ContactPartnershipStyles.smallprint}>
                        <Text source={`<small>${t("contact_partnership_form_smallprint")}</small>`} />
                    </div>
                    <p className={ContactPartnershipStyles.asterisk}>
                        <span style={{ color: "#bf4751" }}>* </span>
                        {t("contact_partnership_form_required_field")}
                    </p>
                    <IconAction title={t("contact_partnership_form_button")} button submit />
                </form>
            ) : (
                <div className={ContactPartnershipStyles.success} id="success_message">
                    <div className={ContactPartnershipStyles.circle}>
                        <i className="lavita-icon"></i>
                    </div>
                    <Text
                        small
                        source={`<strong>${t("contact_partnership_form_success")}</strong>`}
                        align={{ desktop: "center", tablet: "center", mobile: "center" }}
                    />
                </div>
            )}
        </div>
    )
}

export default ContactPartnership
